/**
 * B709 leg 1 — the storefront's Memberships page (Figma 632:2 @1440, 634:77
 * @390): the pure half. It reads the plans ON SALE (`membership.plan.list`
 * under the guest's CUSTOMER token — a buyer sees only ACTIVE plans) and
 * shapes them the way the frame does: the household tiers compared side by
 * side, a LIMITED tier (one that keeps a blackout calendar) on its own card,
 * the 65-and-over tiers in one strip, the add-ons in another, what every
 * plan shares, and the sentences that are TRUE of the model. Anything the
 * frame shows that the platform does not hold — a "most popular" flag, the
 * Wallet, reciprocal admission, a refund policy, the tenant's switches (not
 * readable by a buyer yet) — is left out rather than invented.
 */
import { formatMoney, type Money } from "./money.js";

/* ------------------------------------------------------------------ route */

export interface MembershipsRoute {
  readonly tenantId?: string;
  readonly siteKey?: string;
}

/** `/memberships?tenant=<id>` (or `?siteKey=`); anything else is not this page. */
export function membershipsRouteFrom(loc: { readonly pathname: string; readonly search: string }): MembershipsRoute | undefined {
  if (!/\/memberships\/?$/.test(loc.pathname)) return undefined;
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(loc.search);
  } catch {
    params = new URLSearchParams();
  }
  const tenantId = params.get("tenant") ?? "";
  const siteKey = params.get("siteKey") ?? "";
  return { ...(tenantId !== "" ? { tenantId } : {}), ...(siteKey !== "" ? { siteKey } : {}) };
}

/* ------------------------------------------------------------------- read */

export interface OfferTier {
  readonly tierId: string;
  readonly name: string;
  readonly price: Money;
  readonly covers: { readonly namedAdults: number; readonly householdChildren: { readonly max: number; readonly ageBand?: { readonly min: number; readonly max: number } }; readonly guestsPerDay: number };
  readonly admission: { readonly scope: "ALL" | "LISTED"; readonly blackoutCalendarId?: string };
  readonly benefits: { readonly percentOffByCategory: Readonly<Record<string, number>>; readonly guestPasses: number; readonly parking: boolean; readonly earlyHours: boolean; readonly events: boolean };
  readonly eligibility: { readonly seniorAge?: number };
  readonly addOns: readonly { readonly addOnId: string; readonly name: string; readonly price: Money }[];
}

export interface OfferPlan {
  readonly planId: string;
  readonly name: string;
  readonly kind: "ANNUAL" | "MONTHLY";
  readonly status: string;
  readonly tiers: readonly OfferTier[];
}

/** The plans a buyer may see, read defensively off the wire (a malformed row is dropped, never shown half). */
export function offerPlans(res: unknown): readonly OfferPlan[] {
  const plans = (res as { plans?: unknown } | null)?.plans;
  if (!Array.isArray(plans)) return [];
  return plans.filter(
    (p): p is OfferPlan =>
      typeof p === "object" && p !== null && (p as OfferPlan).status === "ACTIVE" && ((p as OfferPlan).kind === "ANNUAL" || (p as OfferPlan).kind === "MONTHLY") && Array.isArray((p as OfferPlan).tiers),
  );
}

/* ------------------------------------------------------------------ shape */

export type Term = "ANNUAL" | "MONTHLY";

export interface TierLine {
  readonly text: string;
  /** false = the tier lacks what another tier on the page has (the frame's faint "–" row). */
  readonly has: boolean;
}

export interface TierCard {
  readonly tierId: string;
  readonly name: string;
  /** "$189" — whole units when there are no cents. */
  readonly price: string;
  /** "/ year" | "/ month". */
  readonly per: string;
  /** "or $17.25/mo on a 12-month plan" — the same tier on a monthly plan; absent when there is none. */
  readonly monthly?: string;
  readonly covers: readonly string[];
  readonly gets: readonly TierLine[];
  /** One line for the phone list (634:77): "2 adults + children · 4 guest passes · 15% off". */
  readonly summary: string;
}

export interface MembershipsOffer {
  /** Both kinds on sale → the PAY YEARLY / PAY MONTHLY switch. */
  readonly terms: readonly Term[];
  readonly title: string;
  readonly sub: string;
  readonly tiers: readonly TierCard[];
  readonly limited: readonly (TierCard & { readonly blurb: string })[];
  readonly seniors?: { readonly age: number; readonly rows: readonly { readonly name: string; readonly price: string }[] };
  readonly addOns?: { readonly universal: boolean; readonly rows: readonly { readonly name: string; readonly price: string }[] };
  readonly includes: readonly string[];
  readonly beforeYouBuy: readonly string[];
}

const whole = (m: Money): string => formatMoney(m).replace(/\.00$/, "");
const joinAnd = (xs: readonly string[]): string => (xs.length <= 1 ? (xs[0] ?? "") : `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]!}`);
const plural = (n: number, one: string, many: string): string => `${String(n)} ${n === 1 ? one : many}`;
const months = (term: Term): number => (term === "ANNUAL" ? 12 : 1);

function topRate(t: OfferTier): { readonly pct: number; readonly cats: readonly string[] } | undefined {
  const rates = Object.entries(t.benefits.percentOffByCategory).filter(([, bps]) => bps > 0);
  if (rates.length === 0) return undefined;
  const top = Math.max(...rates.map(([, bps]) => bps));
  return { pct: top / 100, cats: rates.filter(([, bps]) => bps === top).map(([c]) => c) };
}

const underAge = (t: OfferTier): string => {
  const band = t.covers.householdChildren.ageBand;
  return band !== undefined ? ` (under ${String(band.max + 1)})` : "";
};

function coversLines(t: OfferTier): string[] {
  return [
    plural(t.covers.namedAdults, "named adult", "named adults"),
    ...(t.covers.householdChildren.max > 0 ? [`Children in your household${underAge(t)}`] : []),
    t.benefits.guestPasses > 0 ? plural(t.benefits.guestPasses, "guest pass", "guest passes") : "No guest passes",
    ...(t.covers.guestsPerDay > 0 ? [`${plural(t.covers.guestsPerDay, "guest", "guests")} free every visit`] : []),
  ];
}

function getsLines(t: OfferTier, all: readonly OfferTier[], term: Term): TierLine[] {
  const rate = topRate(t);
  const anyRate = all.some((x) => topRate(x) !== undefined);
  const evenings = t.benefits.events ? `Member-only evenings${t.benefits.earlyHours ? " + early hours" : ""}` : t.benefits.earlyHours ? "Early hours" : undefined;
  const anyEvenings = all.some((x) => x.benefits.events || x.benefits.earlyHours);
  const lines: TierLine[] = [{ text: t.admission.scope === "ALL" ? `Unlimited visits, ${String(months(term))} month${term === "ANNUAL" ? "s" : ""}` : "Visits to the listed admissions", has: true }];
  if (rate !== undefined) lines.push({ text: `${String(rate.pct)}% off at the ${joinAnd(rate.cats)}`, has: true });
  else if (anyRate) lines.push({ text: "Member prices", has: false });
  if (evenings !== undefined) lines.push({ text: evenings, has: true });
  else if (anyEvenings) lines.push({ text: "Member-only evenings", has: false });
  if (t.benefits.guestPasses > 0) lines.push({ text: plural(t.benefits.guestPasses, "one-time guest pass", "one-time guest passes"), has: true });
  else if (all.some((x) => x.benefits.guestPasses > 0)) lines.push({ text: "Guest passes", has: false });
  if (t.benefits.parking) lines.push({ text: "Preferred parking", has: true });
  else if (all.some((x) => x.benefits.parking)) lines.push({ text: "Preferred parking", has: false });
  return lines;
}

function summaryOf(t: OfferTier): string {
  const rate = topRate(t);
  const who = `${plural(t.covers.namedAdults, "adult", "adults")}${t.covers.householdChildren.max > 0 ? " + children" : ""}`;
  return [
    who,
    t.benefits.guestPasses > 0 ? plural(t.benefits.guestPasses, "guest pass", "guest passes") : "no guest passes",
    ...(rate !== undefined ? [`${String(rate.pct)}% off`] : []),
    ...(t.benefits.earlyHours ? ["early hours"] : []),
    ...(t.benefits.parking ? ["parking"] : []),
  ].join(" · ");
}

export function membershipsOffer(plans: readonly OfferPlan[], term: Term): MembershipsOffer | undefined {
  const terms: Term[] = (["ANNUAL", "MONTHLY"] as const).filter((k) => plans.some((p) => p.kind === k && p.tiers.length > 0));
  if (terms.length === 0) return undefined;
  const kind = terms.includes(term) ? term : terms[0]!;
  const tiers = plans.filter((p) => p.kind === kind).flatMap((p) => p.tiers);
  const monthlyByName = new Map(plans.filter((p) => p.kind === "MONTHLY").flatMap((p) => p.tiers.map((t) => [t.name, t.price] as const)));
  const seniors = tiers.filter((t) => t.eligibility.seniorAge !== undefined);
  const household = tiers.filter((t) => t.eligibility.seniorAge === undefined);
  const main = household.filter((t) => t.admission.blackoutCalendarId === undefined);
  const limitedTiers = household.filter((t) => t.admission.blackoutCalendarId !== undefined);
  const per = kind === "ANNUAL" ? "/ year" : "/ month";
  const card = (t: OfferTier): TierCard => {
    const monthly = kind === "ANNUAL" ? monthlyByName.get(t.name) : undefined;
    return {
      tierId: t.tierId,
      name: t.name,
      price: whole(t.price),
      per,
      ...(monthly !== undefined ? { monthly: `or ${formatMoney(monthly)}/mo on a 12-month plan` } : {}),
      covers: coversLines(t),
      gets: getsLines(t, main.length > 0 ? main : household, kind),
      summary: summaryOf(t),
    };
  };
  const span = kind === "ANNUAL" ? "12 months" : "a month";
  const anyRate = household.some((t) => topRate(t) !== undefined);
  const anyPasses = household.some((t) => t.benefits.guestPasses > 0);
  const allChildren = main.length > 0 && main.every((t) => t.covers.householdChildren.max > 0);
  const subParts = [`${household.some((t) => t.admission.scope === "ALL") ? "Unlimited visits" : "Visits"} for ${span}`, ...(anyRate ? ["member prices"] : []), ...(anyPasses ? ["guest passes to bring friends"] : [])];
  const adds = new Map<string, { name: string; price: Money; count: number }>();
  for (const t of household) for (const a of t.addOns) adds.set(a.addOnId, { name: a.name, price: a.price, count: (adds.get(a.addOnId)?.count ?? 0) + 1 });
  const base = main.length > 0 ? main : household;
  const includes = [
    ...(base.every((t) => t.admission.scope === "ALL") ? [`Unlimited visits for ${span}`] : []),
    ...(base.every((t) => topRate(t) !== undefined) ? ["Member prices"] : []),
    ...(base.every((t) => t.benefits.events) ? ["Member-only evenings"] : []),
    ...(base.every((t) => t.benefits.parking) ? ["Preferred parking"] : []),
  ];
  const children = household.find((t) => t.covers.householdChildren.max > 0);
  return {
    terms,
    title: kind === "ANNUAL" ? "A year of visits, one card" : "A month of visits, one card",
    sub: `${joinAnd(subParts)}. Pick the plan that fits your household${allChildren ? " — every plan covers the children living with you" : ""}.`,
    tiers: main.map(card),
    limited: limitedTiers.map((t) => ({
      ...card(t),
      blurb: "Not valid on some days — the gate tells you before you drive.",
    })),
    ...(seniors.length > 0
      ? { seniors: { age: Math.min(...seniors.map((t) => t.eligibility.seniorAge!)), rows: seniors.map((t) => ({ name: t.name, price: whole(t.price) })) } }
      : {}),
    ...(adds.size > 0 ? { addOns: { universal: [...adds.values()].every((a) => a.count === household.length), rows: [...adds.values()].map((a) => ({ name: a.name, price: `+${whole(a.price)}` })) } } : {}),
    includes: includes.length >= 2 ? includes : [],
    beforeYouBuy: [
      "Named adults can't be changed mid-term.",
      "Renewing early extends from your current expiry — you never lose days.",
      ...(children !== undefined ? [`Children are covered while they live in your household${children.covers.householdChildren.ageBand !== undefined ? ` and are under ${String(children.covers.householdChildren.ageBand.max + 1)}` : ""}.`] : []),
    ],
  };
}

/** The shut CHOOSE door's reason, until the buyer's purchase door lands (B709 (ii)). */
export const MEMBERSHIP_SIGNUP_SHUT = "Sign up at any ticket window — online sign-up is on its way.";
